import { create } from 'zustand';

/**
 * The sidebar renders a single fixed rail. Entering Settings, the inbox
 * (Conversations) or a specific program slides an internal panel over the main
 * navigation inside that same rail — there is no separate secondary sidebar and
 * no icon-rail collapse anymore. `activePanel` is the only piece of panel state;
 * it is derived from the URL by the sidebar, so it is never persisted.
 */
export type SidebarPanel = 'settings' | 'conversations' | 'program' | null;

/** Payload the program-detail pages seed for the program panel's title. */
interface ProgramNavData {
  id: string;
  name: string;
}

/**
 * Single source of truth for the route → internal-panel mapping. Used by the
 * sidebar's URL sync, the store's initial state, and the history tracker.
 * `/programs/browse` is a regular page (main nav), not the program panel.
 */
export const resolvePanelForPath = (path: string): SidebarPanel => {
  if (path.startsWith('/messages')) return 'conversations';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/programs/') && !path.startsWith('/programs/browse')) return 'program';
  return null;
};

/** True when the path lives behind an internal sidebar panel. */
export const isPanelSectionPath = (path: string): boolean => resolvePanelForPath(path) !== null;

/** Full-bleed routes hide the whole sidebar + top bar for editor canvases. */
export const isFullBleedPath = (path: string): boolean =>
  path.startsWith('/flows/') || path === '/brand-space/studio';

/** The menu shows beside the content unless the layout is mobile or the route is full-bleed. */
export const isPrimaryMenuShown = (isMobile: boolean, path: string): boolean =>
  !isMobile && !isFullBleedPath(path);

interface NavBarState {
  /** Which internal panel is slid over the main navigation, or null for the main nav. */
  activePanel: SidebarPanel;
  /** Program shown by the program panel (id + name for the panel title). */
  currentData: ProgramNavData | null;
  /** Last non-panel path, used to route "Back" out of a panel. */
  previousPath: string | null;
  /** Set by the sidebar's URL sync effect — the URL is the source of truth. */
  setActivePanel: (panel: SidebarPanel) => void;
  setData: (data: ProgramNavData) => void;
  clearData: () => void;
  setPreviousPath: (path: string) => void;
}

const getInitialPanel = (): SidebarPanel => {
  if (typeof window === 'undefined') return null;
  return resolvePanelForPath(window.location.pathname);
};

const useNavBarStore = create<NavBarState>()((set) => ({
  activePanel: getInitialPanel(),
  currentData: null,
  previousPath: null,
  setActivePanel: (panel) => set({ activePanel: panel }),
  setData: (data) => set({ currentData: data }),
  clearData: () => set({ currentData: null }),
  setPreviousPath: (path) => set({ previousPath: path }),
}));

export default useNavBarStore;
